import React, { useEffect, useState, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { fetchMyFacilities, setMyFacility } from "actions/Reports";
import { switchSite } from "actions/Auth";
import { searchUsers, setSearchUsers } from "actions/Users";
import { fetchBookmarkedFacilities, setGeneralLoader } from "actions/App";
import { rentalStateLabels, searchableNavigation } from 'utilities';
import { useModal } from "context/ModalContext";
import { useNavigation } from 'context/NavigationContext';
import UnitsUsersDetailModal from "app/routes/Sites/routes/Display/UnitsUsers/UnitsUsersDetailModal";
import { toastManager } from "components/ToastNotification";
import TableNew from 'components/TableNew';
import GlobalSearchInput from "components/GlobalSearchInput";
import Popout from "components/Popout"
import Icon from "components/Icon";
import Tag from "components/Tag";
import LoadingIndicator from "components/LoadingIndicator";

const Header = () => {
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const { openModal } = useModal();
  const { navigateWithHighlight } = useNavigation();
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const popoutAnchorRef = useRef(null);

  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);
  const [isPopoutOpen, setIsPopoutOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [navigationResults, setNavigationResults] = useState([]);

  const authUser = useSelector(state => state.auth.authUser);
  const myFacilities = useSelector(state => state.reports.myFacilities);
  const myFacility = useSelector(state => state.reports.myFacility);
  const site = useSelector(state => state.auth.site);
  const bookmarks = useSelector((state) => state.app.bookmarks);
  const users = useSelector((state) => state.users.searchUsers);

  const handleRowClick = async (item, rowElement) => {

    if (item.userUUID) {
      const unitUser = {
        id: item.unitID,
        uuid: item.unitUUID,
        User: { id: item.userID, uuid: item.userUUID },
      };
      if (item.siteUUID !== site.uuid) {
        dispatch(setGeneralLoader({ modal: true, text: intl.formatMessage({ id: "search.assignUnit.loading" }) }));
        setIsPopoutOpen(false);
        try {
          await new Promise((resolve, reject) => {
            dispatch(switchSite(
              item.siteUUID,
              false,
              false,
              false,
              false,
              false,
              null,
              resolve,
              reject
            ))
          });
          unitUser && openModal(
            <UnitsUsersDetailModal
              trigger={`${unitUser.userUUID}-${Date.now()}`}
              unitUser={unitUser}
            />
          );
        } catch (error) {
          console.error("Error switching site:", error);
          return;
        }
      } else {
        setIsPopoutOpen(false);
        unitUser && openModal(
          <UnitsUsersDetailModal
            trigger={`${unitUser.userUUID}-${Date.now()}`}
            unitUser={unitUser}
          />
        );
      }
    }
  }

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateFade = () => {
      setFadeLeft(el.scrollLeft >= 0.5);
      setFadeRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    updateFade(); // try once immediately
    const timeout = setTimeout(updateFade, 0); // then once after render

    el.addEventListener("scroll", updateFade);
    return () => {
      el.removeEventListener("scroll", updateFade);
      clearTimeout(timeout);
    };
  }, [myFacilities, location.pathname]);

  useEffect(() => {
    dispatch(fetchBookmarkedFacilities());
    dispatch(fetchMyFacilities());
  }, [dispatch]);

  const searchNavigation = (searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') return [];

    const term = searchTerm.toLowerCase().trim();

    const filteredNavigation = searchableNavigation.filter(item => item.label.toLowerCase().includes(term));

    setNavigationResults(filteredNavigation);
  };

  const handleSearch = (text) => {
    let trimmedText = text.trim();
    if (trimmedText.length === 0) {
      setIsPopoutOpen(false);
      return;
    }
    if (trimmedText.length < 3) {
      toastManager.addToast(intl.formatMessage({ id: "notifications.error.blank.searchText" }));
      setIsPopoutOpen(false);
      return;
    }
    // Clear previous results before starting new search
    dispatch(setSearchUsers(null));
    setNavigationResults([]);
    
    setIsPopoutOpen(true);
    setSearchTerm(trimmedText);
    searchNavigation(trimmedText);
    let requestData = {
      keyword: trimmedText,
      ...(!isAllFacilities && !isMyFacilities && { siteID: site.id })
    }
    dispatch(searchUsers(requestData));
  }

  const handleNavigationClick = (item) => {
    setIsPopoutOpen(false);
    setSearchTerm("");
    const targetPath = item.path.replace(":siteId", site.id);
    navigateWithHighlight(targetPath, item.highlightTarget, item.scrollOptions);
  }

  // Determine if active
  const isAllFacilities = location.pathname === "/app/facilities/allFacilities";
  const isMyFacilities = location.pathname === "/app/facilities/myFacilities";
  const isFacilityDetails = location.pathname.includes("/app/facilities/facility/");
  const isGeneralHelp = location.pathname.includes("/app/generalHelp");

  return (
    <div className="flex items-center h-25 justify-between bg-[var(--color-glass-dark)] relative">
      <Popout
        isOpen={isPopoutOpen}
        onClose={() => {
          dispatch(setSearchUsers(null));
          setNavigationResults([]);
          setIsPopoutOpen(false);
        }}
        anchorRef={popoutAnchorRef}
        positioning="bottom-left"
        stickPosition={true}
        header={false}>
        <div className="pt-6 min-w-[800px] max-w-[60vw]">
          {!users && navigationResults.length === 0 ?
            <div className="flex justify-center">
              <LoadingIndicator />
            </div> :
            <div>
              <TableNew
                defaultSortKey="userFirstName"
                data={users?.tenants || []}
                tableView={true}
                showHeaders={true}
                maxHeight={"max-h-[240px]"}
                headerBackgroundColor="--color-gray-50"
                headerSlim={true}
                rowClickAction={(item, e) => handleRowClick(item, e.currentTarget)}
                columns={[
                  {
                    key: "tenants",
                    label: "Tenants",
                    width: "370px",
                    sortable: false,
                    render: (e) => {
                      const name = e.userFirstName + " " + e.userLastName
                      return name && name !== "" && name !== " " ? (
                        <div className="flex flex-col gap-1">
                          {HighlightMatch(name, searchTerm)}
                          <p className="tw-desc-reg font-light text-[var(--color-gray-600)]">{e.userRoleName}</p>
                        </div>
                      ) : null
                    }
                  },
                  {
                    key: "status",
                    label: "Status",
                    sortable: false,
                    render: (e) => {
                      const rentalStateLabel = rentalStateLabels[e.unitRentalState]?.text || "";
                      const rentalStateColor = rentalStateLabels[e.unitRentalState]?.color || "";
                      if (rentalStateLabel === "" || rentalStateColor === "" || e.unitRentalState === "service") {
                        return null;
                      }
                      return <div>
                        <Tag text={rentalStateLabel} color={rentalStateColor} />
                      </div>;
                    }
                  },
                  {
                    key: "units",
                    label: "Units",
                    sortable: false,
                    render: (e) => {
                      return HighlightMatch(e.unitName, searchTerm)
                    }
                  },
                  {
                    key: "location",
                    label: "Location",
                    align: "justify-end",
                    sortable: false,
                    render: (e) => (<p className="tw-paragraph-reg">{e.siteName}</p>)
                  }
                ]}
              />
              <TableNew
                defaultSortKey="userFirstName"
                data={users?.nonTenants || []}
                tableView={true}
                showHeaders={true}
                maxHeight={"max-h-[240px]"}
                headerBackgroundColor="--color-gray-50"
                headerSlim={true}
                rowClickAction={(item, e) => handleRowClick(item, e.currentTarget)}
                columns={[
                  {
                    key: "nonTenants",
                    label: "Non-Tenants",
                    sortable: false,
                    render: (e) => {
                      const name = e.userFirstName + " " + e.userLastName
                      return name && name !== "" && name !== " " ? (
                        <div className="flex flex-col gap-1">
                          {HighlightMatch(name, searchTerm)}
                          <p className="tw-desc-reg font-light text-[var(--color-gray-600)]">{e.userRoleName}</p>
                        </div>
                      ) : null
                    }
                  },
                  {
                    key: "location",
                    label: "Location",
                    align: "justify-end",
                    sortable: false,
                    render: (e) => (<p className="tw-paragraph-reg">{e.siteName}</p>)
                  }
                ]}
              />
              <TableNew
                defaultSortKey="unitName"
                data={users?.availableUnits || []}
                tableView={true}
                showHeaders={true}
                maxHeight={"max-h-[240px]"}
                headerBackgroundColor="--color-gray-50"
                headerSlim={true}
                rowClickAction={(item, e) => handleRowClick(item, e.currentTarget)}
                columns={[
                  {
                    key: "availableUnits",
                    label: "Available Units",
                    sortable: false,
                    render: (e) => {
                      const name = e.unitName
                      return name && name !== "" ? (
                        <div className="flex flex-col gap-1">
                          {HighlightMatch(name, searchTerm)}
                        </div>
                      ) : null
                    }
                  },
                  {
                    key: "location",
                    label: "Location",
                    align: "justify-end",
                    sortable: false,
                    render: (e) => (<p className="tw-paragraph-reg">{e.siteName}</p>)
                  }
                ]}
              />
              {navigationResults.length > 0 && <TableNew
                defaultSortKey="id"
                data={navigationResults || []}
                tableView={true}
                showHeaders={true}
                maxHeight={"max-h-[240px]"}
                headerBackgroundColor="--color-gray-50"
                headerSlim={true}
                rowClickAction={(item) => handleNavigationClick(item)}
                columns={[
                  {
                    key: "label",
                    label: intl.formatMessage({ id: "pages.settings.Types" }),
                    sortable: false,
                    render: (e) => {
                      return HighlightMatch(e.label, searchTerm)

                    }
                  },
                  {
                    key: "location",
                    label: intl.formatMessage({ id: "pages.settings.location" }),
                    align: "justify-end",
                    sortable: false,
                    render: (e) => (<p className="tw-paragraph-reg">{e.breadcrumbs.join(' > ')}</p>)
                  }
                ]}
              />}
            </div>}
        </div>
      </Popout>

      {/* Navigation Tabs */}
      {
        isAllFacilities || isMyFacilities
          ? <div className="flex items-center gap-6 ml-1">
            {((authUser.sites.length > bookmarks.maxBookmarks) ||
              (bookmarks.bookmarkedFacilities.size > 0 && authUser.sites.length > bookmarks.bookmarkedFacilities.size)) && <h5
                id="all-facilities-h5-link"
                data-cy="all-facilities-h5-link"
                className={classNames(
                  "tw-heading-5 cursor-pointer",
                  isAllFacilities ? "text-[var(--color-gray-800)]" : "text-[var(--color-gray-400)]"
                )}
                onClick={() => history.push("/app/facilities/allFacilities")}
              >
                All Facilities
              </h5>}

            <h5
              id="my-facilities-h5-link"
              data-cy="my-facilities-h5-link"
              className={classNames(
                "tw-heading-5",
                ((authUser.sites.length > bookmarks.maxBookmarks) || (bookmarks.bookmarkedFacilities.size > 0 && authUser.sites.length > bookmarks.bookmarkedFacilities.size)) ? "cursor-pointer" : "",
                isMyFacilities ? "text-[var(--color-gray-800)]" : "text-[var(--color-gray-400)]"
              )}
              onClick={() => history.push("/app/facilities/myFacilities")}
            >
              My Facilities
            </h5>
          </div>
          : ((myFacilities && myFacilities.length > 1) || (authUser.sites.length > 1))
            ? <div className="relative flex">
              {/* Back Arrow */}
              <div
                className="px-3 cursor-pointer flex items-center justify-center"
                onClick={() => {
                  dispatch(fetchMyFacilities());
                  history.push("/app/facilities/myFacilities");
                }}
                id="Header-Back-Button"
                data-cy="Header-Back-Button">
                <Icon
                  name="arrowLeft"
                  size={24}
                  className="text-[var(--color-gray-800)]"
                />
              </div>

              {/* Scrollable Facility Tabs */}
              <div className="relative max-w-[calc(100vw-700px)]">
                {/* Arrows */}
                {fadeLeft && (
                  <button
                    onClick={() => scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' })}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 text-2xl"
                    aria-label="Scroll left"
                  >
                    ‹
                  </button>
                )}
                {fadeRight && (
                  <button
                    onClick={() => scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' })}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 text-2xl"
                    aria-label="Scroll right"
                  >
                    ›
                  </button>
                )}

                <div
                  ref={scrollRef}
                  className="flex items-center gap-6 ml-1 overflow-x-auto overflow-y-hidden scrollbar-hide scroll-smooth snap-x snap-mandatory"
                  style={{
                    WebkitMaskImage: fadeLeft && fadeRight
                      ? 'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)'
                      : fadeLeft
                        ? 'linear-gradient(to right, transparent 0%, black 5%)'
                        : fadeRight
                          ? 'linear-gradient(to right, black 95%, transparent 100%)'
                          : 'none',
                    WebkitMaskRepeat: 'no-repeat',
                    maskImage: fadeLeft && fadeRight
                      ? 'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)'
                      : fadeLeft
                        ? 'linear-gradient(to right, transparent 0%, black 5%)'
                        : fadeRight
                          ? 'linear-gradient(to right, black 95%, transparent 100%)'
                          : 'none',
                    maskRepeat: 'no-repeat',
                    transition: 'mask-image 0.3s ease',
                  }}
                >
                  {myFacilities && myFacilities.length > 1 &&
                    myFacilities
                      .filter(m => m.id != (myFacility?.id ?? authUser?.defaultSiteId))
                      .map((f) =>
                        <h6
                          key={`${f.name}-${f.id}`}
                          id={`facility-${f.id}-h5-link`}
                          data-cy={`facility-${f.id}-h5-link`}
                          className="tw-heading-6 whitespace-nowrap cursor-pointer shrink-0 text-[var(--color-gray-600)]"
                          onClick={() => {
                            dispatch(setMyFacility(null));
                            dispatch(setGeneralLoader({ modal: true, text: intl.formatMessage({ id: "comp.button.switchingSite" }) }));
                            dispatch(switchSite(f.id.toString(), false, false, false, false, `/app/facilities/facility/${f.id}/overview`));
                          }}
                        >
                          {f.name}
                        </h6>
                      )
                  }
                </div>
              </div>
            </div>
            : null
      }
      {
        !isAllFacilities && !isMyFacilities && !isFacilityDetails && !isGeneralHelp && <div>
          <h3 className="tw-heading-3 mr-8">{site?.name}</h3>
        </div>
      }
      {
        (authUser.featureflagAssignments.includes('new_ui_v3')) &&
        (isAllFacilities || isMyFacilities || isFacilityDetails) &&
        authUser.role.tier > 1 &&
        <div className="ml-auto">
          <GlobalSearchInput
            ref={popoutAnchorRef}
            onSearch={handleSearch}
            placeholder={
              (!isAllFacilities && !isMyFacilities)
                ? intl.formatMessage({ id: "comp.search.usersMore" })
                : intl.formatMessage({ id: "comp.search.users" })
            }
            id="Global-Search-Input"
            dataCy="Global-Search-Input"
          />
        </div>
      }
    </div >
  );
};

function HighlightMatch(name, searchTerm) {
  if (!name || !searchTerm) return <p className="tw-desc-reg font-light"></p>;
  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedSearchTerm})`, 'i'); // case-insensitive match
  const parts = name.split(regex);
  return (
    <p className="tw-desc-reg font-light">
      {parts.map((part, index) =>
        regex.test(part) ? (
          <span key={index} style={{ fontWeight: 'bold' }}>
            {part}
          </span>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </p>
  );
}

export default Header;